import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { User, Home, Calendar, Download, Settings, LogOut, ChevronRight, Edit2, Camera, Clock, MapPin, Phone, Mail, Building, Check, X, Eye, Lock, RefreshCw, Plus, Upload, Image, ExternalLink, ChevronDown, MoreVertical, DollarSign, Briefcase, Hash, AlertTriangle, ArrowRight, Bell } from 'lucide-react';
import ClientPropertyPayment from './payments/ClientPropertyPayment';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const ClientPortal = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState('properties');
  const [allProperties, setAllProperties] = useState([]);
  const [allAppointments, setAllAppointments] = useState([]);
  const [profile, setProfile] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showBusinessDropdown, setShowBusinessDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [toast, setToast] = useState(null);
  const [timeProposals, setTimeProposals] = useState([]);
  const dropdownRef = useRef(null);

  // Filter properties and appointments by selected business
  const properties = selectedBusiness 
    ? allProperties.filter(p => p.business_id === selectedBusiness.id)
    : allProperties;
  
  const appointments = selectedBusiness
    ? allAppointments.filter(a => a.business_id === selectedBusiness.id)
    : allAppointments;

  useEffect(() => {
    loadData();
  }, []);

  // Set default selected business when businesses load
  useEffect(() => {
    if (businesses.length > 0 && !selectedBusiness) {
      setSelectedBusiness(businesses[0]);
    }
  }, [businesses]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowBusinessDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [propertiesRes, appointmentsRes, profileRes, businessesRes, proposalsRes] = await Promise.all([
        axios.get(`${API}/client-portal/properties`).catch(() => ({ data: [] })),
        axios.get(`${API}/client-portal/appointments`).catch(() => ({ data: [] })),
        axios.get(`${API}/client-portal/profile`).catch(() => ({ data: null })),
        axios.get(`${API}/client-portal/businesses`).catch(() => ({ data: [] })),
        axios.get(`${API}/client-portal/time-proposals`).catch(() => ({ data: [] }))
      ]);
      
      setAllProperties(propertiesRes.data || []);
      setAllAppointments(appointmentsRes.data || []);
      setProfile(profileRes.data);
      setBusinesses(businessesRes.data || []);
      setTimeProposals(proposalsRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    delete axios.defaults.headers.common['Authorization'];
    onLogout();
  };

  const scheduleNewAppointment = () => {
    if (!selectedBusiness) return;
    
    // Store client info in session storage for pre-filling the booking form
    const clientData = {
      client_name: profile?.user?.full_name || '',
      client_email: profile?.user?.email || '',
      client_phone: profile?.user?.phone || profile?.client?.phone || '',
      client_company: profile?.client?.company || ''
    };
    sessionStorage.setItem('clientPortalBooking', JSON.stringify(clientData));
    
    // Redirect to the public booking page for this business
    window.location.href = `/schedule/${selectedBusiness.id}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg ${
          toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white`}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Business Selector */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowBusinessDropdown(!showBusinessDropdown)}
                className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1.5 transition-colors"
              >
                {selectedBusiness?.logo ? (
                  <img 
                    src={`${API}/media/${selectedBusiness.logo}/thumbnail`}
                    alt={selectedBusiness.name}
                    className="w-10 h-10 rounded-xl object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                    <Building className="w-5 h-5 text-white" />
                  </div>
                )}
                <div className="text-left">
                  <span className="text-xl font-semibold text-gray-900 block">
                    {selectedBusiness?.name || 'Select Business'}
                  </span>
                  {businesses.length > 1 && (
                    <span className="text-xs text-gray-500">Click to switch</span>
                  )}
                </div>
                {businesses.length > 1 && (
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showBusinessDropdown ? 'rotate-180' : ''}`} />
                )}
              </button>

              {/* Dropdown Menu */}
              {showBusinessDropdown && businesses.length > 1 && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                  <div className="px-3 py-2 border-b border-gray-100">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Switch Business</p>
                  </div>
                  {businesses.map(biz => (
                    <button
                      key={biz.id}
                      onClick={() => {
                        setSelectedBusiness(biz);
                        setShowBusinessDropdown(false);
                        setSelectedProperty(null);
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-3 hover:bg-gray-50 transition-colors ${
                        selectedBusiness?.id === biz.id ? 'bg-blue-50' : ''
                      }`}
                    >
                      {biz.logo ? (
                        <img 
                          src={`${API}/media/${biz.logo}/thumbnail`}
                          alt={biz.name}
                          className="w-10 h-10 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                          <Building className="w-5 h-5 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 text-left">
                        <p className="font-medium text-gray-900">{biz.name}</p>
                        {biz.phone && <p className="text-sm text-gray-500">{biz.phone}</p>}
                      </div>
                      {selectedBusiness?.id === biz.id && (
                        <Check className="w-5 h-5 text-blue-600" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                {profile?.user?.full_name || profile?.user?.email}
              </span>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <aside className="lg:w-64 flex-shrink-0">
            <nav className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  {profile?.client?.avatar_path ? (
                    <img 
                      src={`${API}/client-portal/avatar/${profile.user.id}`} 
                      alt="Avatar"
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-blue-600" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-gray-900">{profile?.user?.full_name || 'Client'}</p>
                    <p className="text-sm text-gray-500">{profile?.user?.email}</p>
                  </div>
                </div>
              </div>
              
              <div className="p-2">
                {[
                  { id: 'properties', icon: Home, label: 'My Properties' },
                  { id: 'appointments', icon: Calendar, label: 'Appointments' },
                  { id: 'profile', icon: Settings, label: 'Profile Settings' },
                ].map(item => (
                  <button
                    key={item.id}
                    onClick={() => { setActiveTab(item.id); setSelectedProperty(null); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      activeTab === item.id
                        ? 'bg-blue-50 text-blue-600'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                    <ChevronRight className={`w-4 h-4 ml-auto transition-transform ${
                      activeTab === item.id ? 'rotate-90' : ''
                    }`} />
                  </button>
                ))}
              </div>
            </nav>

            {/* Schedule New Button */}
            {selectedBusiness && (
              <button
                onClick={() => scheduleNewAppointment()}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 mt-4 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-colors font-medium"
              >
                <Plus className="w-5 h-5" />
                Schedule New
              </button>
            )}
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Time Proposals Banner - Show prominently when there are pending proposals */}
            {timeProposals.length > 0 && (
              <TimeProposalsBanner 
                proposals={timeProposals}
                onRefresh={loadData}
                showToast={showToast}
              />
            )}

            {activeTab === 'properties' && !selectedProperty && (
              <PropertiesTab 
                properties={properties} 
                onSelectProperty={setSelectedProperty}
                onRefresh={loadData}
              />
            )}
            
            {activeTab === 'properties' && selectedProperty && (
              <PropertyDetail 
                property={selectedProperty}
                onBack={() => setSelectedProperty(null)}
                onRefresh={loadData}
                showToast={showToast}
              />
            )}
            
            {activeTab === 'appointments' && (
              <AppointmentsTab 
                appointments={appointments}
                businesses={businesses}
                onRefresh={loadData}
                showToast={showToast}
                onScheduleNew={scheduleNewAppointment}
              />
            )}
            
            {activeTab === 'profile' && (
              <ProfileTab 
                profile={profile}
                onRefresh={loadData}
                showToast={showToast}
              />
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

// Time Proposals Banner Component
const TimeProposalsBanner = ({ proposals, onRefresh, showToast }) => {
  const [responding, setResponding] = useState(null);
  const [expandedProposal, setExpandedProposal] = useState(null);

  const formatTime = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleRespond = async (proposalId, action) => {
    setResponding(proposalId);
    try {
      const response = await axios.put(`${API}/client-portal/time-proposals/${proposalId}/respond`, { action });
      showToast(response.data.message || (action === 'accept' ? 'New time accepted!' : 'Proposed time declined'));
      onRefresh();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to respond to proposal', 'error');
    } finally {
      setResponding(null);
    }
  };

  if (proposals.length === 0) return null;

  return (
    <div className="mb-6 space-y-4">
      {/* Alert Header */}
      <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
        <div className="p-2 bg-amber-100 rounded-full">
          <Bell className="w-5 h-5 text-amber-600" />
        </div>
        <div>
          <h3 className="font-semibold text-amber-900">Action Required</h3>
          <p className="text-sm text-amber-700">
            You have {proposals.length} booking{proposals.length > 1 ? 's' : ''} with proposed new time{proposals.length > 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Proposal Cards */}
      {proposals.map((proposal) => (
        <div 
          key={proposal.proposal_id}
          className="bg-white rounded-xl shadow-sm border border-amber-200 overflow-hidden"
          data-testid={`time-proposal-${proposal.proposal_id}`}
        >
          {/* Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full uppercase tracking-wide">
                    New Time Proposed
                  </span>
                </div>
                <h4 className="font-semibold text-gray-900">{proposal.property_address}</h4>
                <p className="text-sm text-gray-600">{proposal.business_name}</p>
              </div>
              <button
                onClick={() => setExpandedProposal(expandedProposal === proposal.proposal_id ? null : proposal.proposal_id)}
                className="p-1 hover:bg-amber-100 rounded transition-colors"
              >
                <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${expandedProposal === proposal.proposal_id ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>

          {/* Time Comparison */}
          <div className="p-6">
            <div className="flex flex-col sm:flex-row items-stretch gap-4">
              {/* Original Time */}
              <div className="flex-1 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Originally Requested</p>
                <div className="flex items-center gap-2 text-gray-600 mb-1">
                  <Calendar className="w-4 h-4" />
                  <span className="font-medium">{formatDate(proposal.original_date)}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>{formatTime(proposal.original_time)}</span>
                </div>
              </div>

              {/* Arrow */}
              <div className="flex items-center justify-center py-2">
                <ArrowRight className="w-6 h-6 text-amber-500" />
              </div>

              {/* Proposed New Time */}
              <div className="flex-1 p-4 bg-amber-50 rounded-lg border-2 border-amber-300">
                <p className="text-xs font-medium text-amber-700 uppercase tracking-wide mb-2">Proposed New Time</p>
                <div className="flex items-center gap-2 text-amber-900 mb-1">
                  <Calendar className="w-4 h-4" />
                  <span className="font-semibold">{formatDate(proposal.proposed_date)}</span>
                </div>
                <div className="flex items-center gap-2 text-amber-900">
                  <Clock className="w-4 h-4" />
                  <span className="font-semibold">{formatTime(proposal.proposed_time)}</span>
                </div>
              </div>
            </div>

            {/* Note from business */}
            {proposal.note && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <p className="text-xs font-medium text-blue-700 mb-1">Note from business:</p>
                <p className="text-sm text-blue-900">{proposal.note}</p>
              </div>
            )}

            {/* Expanded Details */}
            {expandedProposal === proposal.proposal_id && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {proposal.property_sqft && (
                    <div>
                      <span className="text-gray-500">Property Size:</span>
                      <span className="ml-2 font-medium">{proposal.property_sqft.toLocaleString()} sqft</span>
                    </div>
                  )}
                  {proposal.product_names && proposal.product_names.length > 0 && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Services:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {proposal.product_names.map((name, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full">
                            {name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <button
                onClick={() => handleRespond(proposal.proposal_id, 'accept')}
                disabled={responding === proposal.proposal_id}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors font-medium"
                data-testid={`accept-proposal-${proposal.proposal_id}`}
              >
                {responding === proposal.proposal_id ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-5 h-5" />
                )}
                Accept New Time
              </button>
              <button
                onClick={() => handleRespond(proposal.proposal_id, 'decline')}
                disabled={responding === proposal.proposal_id}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-white text-red-600 border-2 border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors font-medium"
                data-testid={`decline-proposal-${proposal.proposal_id}`}
              >
                <X className="w-5 h-5" />
                Decline
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Properties Tab Component
const PropertiesTab = ({ properties, onSelectProperty, onRefresh }) => {
  if (properties.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Home className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Properties Yet</h3>
        <p className="text-gray-500 mb-6">Your properties will appear here once you book a service.</p>
        <button
          onClick={onRefresh}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">My Properties</h2>
        <button
          onClick={onRefresh}
          className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {properties.map(property => (
          <div
            key={property.id}
            onClick={() => onSelectProperty(property)}
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer group"
          >
            {/* Property Image */}
            <div className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-200">
              {property.media && property.media.length > 0 ? (
                <img
                  src={`${BACKEND_URL}/api/media/${property.media[0].id}/thumbnail`}
                  alt={property.property_address}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Home className="w-16 h-16 text-gray-300" />
                </div>
              )}
              <div className="absolute top-3 right-3">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  property.status === 'completed' ? 'bg-green-100 text-green-700' :
                  property.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                  property.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {property.status?.replace('_', ' ') || 'Pending'}
                </span>
              </div>
              {property.media_count > 0 && (
                <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-black/60 text-white px-2 py-1 rounded-lg text-sm">
                  <Camera className="w-4 h-4" />
                  <span>{property.media_count}</span>
                </div>
              )}
            </div>

            {/* Property Info */}
            <div className="p-4">
              <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                {property.property_address}
              </h3>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                {property.property_sqft && (
                  <span>{property.property_sqft.toLocaleString()} sqft</span>
                )}
                {property.property_beds && (
                  <span>{property.property_beds} beds</span>
                )}
                {property.property_baths && (
                  <span>{property.property_baths} baths</span>
                )}
              </div>
              {property.scheduled_date && (
                <div className="flex items-center gap-2 mt-3 text-sm text-gray-500">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(property.scheduled_date).toLocaleDateString()}</span>
                  {property.scheduled_time && <span>at {property.scheduled_time}</span>}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Property Detail Component
const PropertyDetail = ({ property, onBack, onRefresh, showToast }) => {
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    property_beds: property.property_beds || '',
    property_baths: property.property_baths || '',
    property_sqft: property.property_sqft || '',
    property_year_built: property.property_year_built || '',
    property_description: property.property_description || '',
    property_price: property.property_price || '',
    notes: property.notes || ''
  });
  const [saving, setSaving] = useState(false);
  const [mediaDetails, setMediaDetails] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(true);

  useEffect(() => {
    loadMediaDetails();
  }, [property.id]);

  const loadMediaDetails = async () => {
    try {
      const response = await axios.get(`${API}/client-portal/property/${property.id}`);
      setMediaDetails(response.data.media || []);
    } catch (error) {
      console.error('Error loading media:', error);
    } finally {
      setLoadingMedia(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/client-portal/property/${property.id}`, formData);
      showToast('Property details updated successfully');
      setEditMode(false);
      onRefresh();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to update property', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (media) => {
    try {
      const response = await axios.get(
        `${API}/client-portal/media/${media.id}/download`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', media.original_filename || `media_${media.id}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast('Download started');
    } catch (error) {
      showToast('Failed to download file', 'error');
    }
  };

  const downloadAllMedia = async () => {
    showToast('Starting downloads...');
    for (const media of mediaDetails) {
      await handleDownload(media);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ChevronRight className="w-5 h-5 rotate-180" />
        </button>
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-gray-900">{property.property_address}</h2>
          <p className="text-gray-500">Property Details</p>
        </div>
        <button
          onClick={() => setEditMode(!editMode)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            editMode ? 'bg-gray-200 text-gray-700' : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          <Edit2 className="w-4 h-4" />
          {editMode ? 'Cancel' : 'Edit Details'}
        </button>
      </div>

      {/* Payment widget — shows balance + saved card + pay buttons */}
      <ClientPropertyPayment appointment={property} onChanged={onRefresh} />

      {/* Property Details Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Property Information</h3>
        
        {editMode ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bedrooms</label>
              <input
                type="number"
                value={formData.property_beds}
                onChange={(e) => setFormData({ ...formData, property_beds: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bathrooms</label>
              <input
                type="number"
                step="0.5"
                value={formData.property_baths}
                onChange={(e) => setFormData({ ...formData, property_baths: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Square Feet</label>
              <input
                type="number"
                value={formData.property_sqft}
                onChange={(e) => setFormData({ ...formData, property_sqft: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year Built</label>
              <input
                type="number"
                value={formData.property_year_built}
                onChange={(e) => setFormData({ ...formData, property_year_built: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
              <input
                type="text"
                value={formData.property_price}
                onChange={(e) => setFormData({ ...formData, property_price: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="$500,000"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={formData.property_description}
                onChange={(e) => setFormData({ ...formData, property_description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="sm:col-span-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {property.property_beds && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Bedrooms</p>
                <p className="text-lg font-semibold text-gray-900">{property.property_beds}</p>
              </div>
            )}
            {property.property_baths && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Bathrooms</p>
                <p className="text-lg font-semibold text-gray-900">{property.property_baths}</p>
              </div>
            )}
            {property.property_sqft && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Square Feet</p>
                <p className="text-lg font-semibold text-gray-900">{property.property_sqft.toLocaleString()}</p>
              </div>
            )}
            {property.property_year_built && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Year Built</p>
                <p className="text-lg font-semibold text-gray-900">{property.property_year_built}</p>
              </div>
            )}
            {property.property_price && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Price</p>
                <p className="text-lg font-semibold text-gray-900">{property.property_price}</p>
              </div>
            )}
            {property.property_description && (
              <div className="sm:col-span-2 lg:col-span-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500 mb-1">Description</p>
                <p className="text-gray-900">{property.property_description}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Media Gallery */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Media Gallery</h3>
          {mediaDetails.length > 0 && (
            <button
              onClick={downloadAllMedia}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Download className="w-4 h-4" />
              Download All
            </button>
          )}
        </div>

        {loadingMedia ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        ) : mediaDetails.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Camera className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No media files available yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {mediaDetails.map(media => (
              <div key={media.id} className="group relative aspect-square rounded-lg overflow-hidden bg-gray-100">
                {media.mime_type?.startsWith('image/') ? (
                  <img
                    src={`${BACKEND_URL}/api/media/${media.id}/thumbnail`}
                    alt={media.original_filename}
                    className="w-full h-full object-cover"
                  />
                ) : media.mime_type?.startsWith('video/') ? (
                  <div className="w-full h-full flex items-center justify-center bg-gray-800">
                    <Camera className="w-12 h-12 text-gray-400" />
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Camera className="w-12 h-12 text-gray-300" />
                  </div>
                )}
                
                {/* Overlay with actions */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={() => window.open(`${BACKEND_URL}/api/media/${media.id}/file`, '_blank')}
                    className="p-2 bg-white/20 hover:bg-white/40 rounded-lg transition-colors"
                    title="View"
                  >
                    <Eye className="w-5 h-5 text-white" />
                  </button>
                  <button
                    onClick={() => handleDownload(media)}
                    className="p-2 bg-white/20 hover:bg-white/40 rounded-lg transition-colors"
                    title="Download"
                  >
                    <Download className="w-5 h-5 text-white" />
                  </button>
                </div>
                
                {/* File name */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                  <p className="text-xs text-white truncate">{media.original_filename}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Appointments Tab Component
const AppointmentsTab = ({ appointments, businesses, onRefresh, showToast, onScheduleNew, selectedBusiness }) => {
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesForm, setNotesForm] = useState('');
  const [saving, setSaving] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(null);
  const [modifyStatus, setModifyStatus] = useState({});
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if appointments can be modified
  useEffect(() => {
    const checkModifyStatus = async () => {
      const statuses = {};
      for (const apt of appointments) {
        if (apt.status !== 'completed' && apt.status !== 'cancelled') {
          try {
            const response = await axios.get(`${API}/client-portal/appointment/${apt.id}/can-modify`);
            statuses[apt.id] = response.data;
          } catch (error) {
            statuses[apt.id] = { can_reschedule: false, can_cancel: false, reason: 'Unable to check status' };
          }
        }
      }
      setModifyStatus(statuses);
    };
    if (appointments.length > 0) {
      checkModifyStatus();
    }
  }, [appointments]);

  const startEditNotes = (apt) => {
    setEditingNotes(apt.id);
    setNotesForm(apt.notes || '');
    setOpenMenuId(null);
  };

  const cancelEditNotes = () => {
    setEditingNotes(null);
    setNotesForm('');
  };

  const saveNotes = async (appointmentId) => {
    setSaving(true);
    try {
      await axios.put(`${API}/client-portal/appointment/${appointmentId}/notes`, { notes: notesForm });
      showToast('Notes updated successfully');
      setEditingNotes(null);
      onRefresh();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to update notes', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReschedule = (apt) => {
    const status = modifyStatus[apt.id];
    if (status && !status.can_reschedule) {
      showToast(status.reason || 'Cannot reschedule this appointment', 'error');
      return;
    }
    
    // Store reschedule data in session storage
    sessionStorage.setItem('rescheduleAppointment', JSON.stringify({
      appointment_id: apt.id,
      business_id: apt.business_id,
      property_address: apt.property_address,
      property_sqft: apt.property_sqft,
      selected_products: apt.selected_products || [],
      selected_addons: apt.selected_addons || [],
      photographer_id: apt.photographer_id,
      service_type: apt.service_type,
      client_name: apt.client_name,
      client_email: apt.client_email,
      client_phone: apt.client_phone,
      special_instructions: apt.special_instructions
    }));
    
    // Redirect to scheduling page in reschedule mode
    window.location.href = `/schedule/${apt.business_id}?reschedule=${apt.id}`;
  };

  const handleCancel = async (appointmentId) => {
    const status = modifyStatus[appointmentId];
    if (status && !status.can_cancel) {
      showToast(status.reason || 'Cannot cancel this appointment', 'error');
      return;
    }
    
    setCancellingId(appointmentId);
    try {
      await axios.post(`${API}/client-portal/appointment/${appointmentId}/cancel`);
      showToast('Appointment cancelled successfully');
      setShowCancelConfirm(null);
      onRefresh();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to cancel appointment', 'error');
    } finally {
      setCancellingId(null);
    }
  };

  const formatTime = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">My Appointments</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={onRefresh}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {appointments.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Appointments</h3>
          <p className="text-gray-500 mb-6">You have not scheduled any appointments yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {appointments.map(apt => {
            const status = modifyStatus[apt.id] || {};
            const canModify = apt.status !== 'completed' && apt.status !== 'cancelled';
            const isLocked = canModify && status.can_reschedule === false && status.can_cancel === false;
            const isMenuOpen = openMenuId === apt.id;
            
            return (
              <div key={apt.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Header with status and menu */}
                <div className={`px-6 py-3 border-b flex items-center justify-between ${
                  apt.status === 'completed' ? 'bg-green-50 border-green-100' :
                  apt.status === 'scheduled' ? 'bg-blue-50 border-blue-100' :
                  apt.status === 'cancelled' ? 'bg-red-50 border-red-100' :
                  apt.status === 'in_progress' ? 'bg-yellow-50 border-yellow-100' :
                  'bg-gray-50 border-gray-100'
                }`}>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${
                      apt.status === 'completed' ? 'bg-green-100 text-green-700' :
                      apt.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                      apt.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                      apt.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {apt.status?.replace('_', ' ') || 'Pending'}
                    </span>
                    {isLocked && (
                      <span className="flex items-center gap-1 text-xs text-amber-600">
                        <Lock className="w-3 h-3" />
                        Locked
                      </span>
                    )}
                  </div>
                  
                  {/* Hamburger Menu */}
                  {canModify && !editingNotes && showCancelConfirm !== apt.id && (
                    <div className="relative" ref={isMenuOpen ? menuRef : null}>
                      <button
                        onClick={() => setOpenMenuId(isMenuOpen ? null : apt.id)}
                        className="p-2 hover:bg-white/50 rounded-lg transition-colors"
                        data-testid={`appointment-menu-${apt.id}`}
                      >
                        <MoreVertical className="w-5 h-5 text-gray-500" />
                      </button>
                      
                      {isMenuOpen && (
                        <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                          <button
                            onClick={() => {
                              handleReschedule(apt);
                              setOpenMenuId(null);
                            }}
                            disabled={!status.can_reschedule}
                            className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left transition-colors ${
                              status.can_reschedule 
                                ? 'text-gray-700 hover:bg-gray-50' 
                                : 'text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            <Calendar className="w-4 h-4" />
                            Reschedule
                          </button>
                          <button
                            onClick={() => {
                              setShowCancelConfirm(apt.id);
                              setOpenMenuId(null);
                            }}
                            disabled={!status.can_cancel}
                            className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left transition-colors ${
                              status.can_cancel 
                                ? 'text-red-600 hover:bg-red-50' 
                                : 'text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            <X className="w-4 h-4" />
                            Cancel Appointment
                          </button>
                          <hr className="my-1 border-gray-100" />
                          <button
                            onClick={() => startEditNotes(apt)}
                            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left text-gray-700 hover:bg-gray-50"
                          >
                            <Edit2 className="w-4 h-4" />
                            {apt.notes ? 'Edit Notes' : 'Add Notes'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Main Content */}
                <div className="p-6">
                  {/* Property Address */}
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{apt.property_address}</h3>
                  
                  {/* Info Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    {/* Date */}
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                        <Calendar className="w-3.5 h-3.5" />
                        Date
                      </div>
                      <p className="font-medium text-gray-900">{formatDate(apt.scheduled_date)}</p>
                    </div>
                    
                    {/* Time */}
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                        <Clock className="w-3.5 h-3.5" />
                        Time
                      </div>
                      <p className="font-medium text-gray-900">{formatTime(apt.scheduled_time) || '-'}</p>
                    </div>
                    
                    {/* Square Footage */}
                    {apt.property_sqft && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                          <Home className="w-3.5 h-3.5" />
                          Sq Ft
                        </div>
                        <p className="font-medium text-gray-900">{apt.property_sqft?.toLocaleString()}</p>
                      </div>
                    )}
                    
                    {/* Price */}
                    {apt.total_price > 0 && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                          <DollarSign className="w-3.5 h-3.5" />
                          Total
                        </div>
                        <p className="font-medium text-gray-900">${apt.total_price?.toFixed(2)}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Additional Details */}
                  <div className="space-y-2 text-sm">
                    {/* Business */}
                    {apt.business_name && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Building className="w-4 h-4 text-gray-400" />
                        <span>{apt.business_name}</span>
                      </div>
                    )}
                    
                    {/* Photographer */}
                    {apt.photographer_name && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Camera className="w-4 h-4 text-gray-400" />
                        <span><span className="font-medium">Photographer:</span> {apt.photographer_name}</span>
                      </div>
                    )}
                    
                    {/* Products/Services Ordered */}
                    {apt.product_names && apt.product_names.length > 0 && (
                      <div className="flex items-start gap-2 text-gray-600">
                        <Briefcase className="w-4 h-4 text-gray-400 mt-0.5" />
                        <div>
                          <span className="font-medium">Services:</span>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {apt.product_names.map((name, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full">
                                {name}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Add-ons */}
                    {apt.addon_names && apt.addon_names.length > 0 && (
                      <div className="flex items-start gap-2 text-gray-600">
                        <Plus className="w-4 h-4 text-gray-400 mt-0.5" />
                        <div>
                          <span className="font-medium">Add-ons:</span>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {apt.addon_names.map((name, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded-full">
                                {name}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Fallback to service_type if no product_names */}
                    {(!apt.product_names || apt.product_names.length === 0) && apt.service_type && (
                      <div className="flex items-start gap-2 text-gray-600">
                        <Briefcase className="w-4 h-4 text-gray-400 mt-0.5" />
                        <span><span className="font-medium">Services:</span> {apt.service_type}</span>
                      </div>
                    )}
                    
                    {/* Special Instructions */}
                    {apt.special_instructions && (
                      <div className="flex items-start gap-2 text-gray-600">
                        <Edit2 className="w-4 h-4 text-gray-400 mt-0.5" />
                        <span><span className="font-medium">Instructions:</span> {apt.special_instructions}</span>
                      </div>
                    )}
                    
                    {/* Booking ID */}
                    <div className="flex items-center gap-2 text-gray-400 text-xs mt-2">
                      <Hash className="w-3 h-3" />
                      <span>ID: {apt.id?.slice(0, 8)}...</span>
                    </div>
                  </div>

                  {/* Notes Section */}
                  {editingNotes === apt.id ? (
                    <div className="mt-4 space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                        <textarea
                          value={notesForm}
                          onChange={(e) => setNotesForm(e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Add notes about this appointment..."
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => saveNotes(apt.id)}
                          disabled={saving}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                          Save Notes
                        </button>
                        <button
                          onClick={cancelEditNotes}
                          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                        >
                          <X className="w-4 h-4" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : apt.notes ? (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                      <p className="text-sm text-blue-700 font-medium mb-1">Your Notes:</p>
                      <p className="text-sm text-blue-900">{apt.notes}</p>
                    </div>
                  ) : null}

                  {/* Locked Notice */}
                  {isLocked && (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-800">
                        <span className="font-medium">Modification Locked:</span> {status.reason || `This appointment cannot be modified within ${status.lockout_hours || 24} hours of the scheduled time.`}
                      </p>
                    </div>
                  )}

                  {/* Cancel Confirmation */}
                  {showCancelConfirm === apt.id && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-800 mb-3 font-medium">Are you sure you want to cancel this appointment?</p>
                      <p className="text-xs text-red-600 mb-3">This action cannot be undone.</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleCancel(apt.id)}
                          disabled={cancellingId === apt.id}
                          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                        >
                          {cancellingId === apt.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
                          Yes, Cancel Appointment
                        </button>
                        <button
                          onClick={() => setShowCancelConfirm(null)}
                          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                        >
                          No, Keep It
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Profile Tab Component with Avatar Upload
const ProfileTab = ({ profile, onRefresh, showToast }) => {
  const [editMode, setEditMode] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [formData, setFormData] = useState({
    full_name: profile?.user?.full_name || '',
    phone: profile?.user?.phone || profile?.client?.phone || '',
    company: profile?.client?.company || ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/client-portal/profile`, formData);
      showToast('Profile updated successfully');
      setEditMode(false);
      onRefresh();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to update profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      showToast('Passwords do not match', 'error');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      showToast('Password must be at least 6 characters', 'error');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/client-portal/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      showToast('Password changed successfully');
      setShowPasswordForm(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to change password', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast('Please select an image file', 'error');
      return;
    }

    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file);

      await axios.post(`${API}/client-portal/avatar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showToast('Avatar uploaded successfully');
      onRefresh();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to upload avatar', 'error');
    } finally {
      setUploadingAvatar(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Profile Settings</h2>

      {/* Avatar Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Profile Photo</h3>
        <div className="flex items-center gap-6">
          <div className="relative">
            {profile?.client?.avatar_path ? (
              <img 
                src={`${API}/client-portal/avatar/${profile.user.id}`} 
                alt="Avatar"
                className="w-24 h-24 rounded-full object-cover"
              />
            ) : (
              <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center">
                <User className="w-10 h-10 text-blue-600" />
              </div>
            )}
            <button
              onClick={() => avatarInputRef.current?.click()}
              disabled={uploadingAvatar}
              className="absolute bottom-0 right-0 p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50"
            >
              {uploadingAvatar ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Camera className="w-4 h-4" />
              )}
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              className="hidden"
            />
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-2">Upload a profile photo</p>
            <button
              onClick={() => avatarInputRef.current?.click()}
              disabled={uploadingAvatar}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              {uploadingAvatar ? 'Uploading...' : 'Change Photo'}
            </button>
          </div>
        </div>
      </div>

      {/* Profile Info Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-semibold text-gray-900">Personal Information</h3>
          <button
            onClick={() => setEditMode(!editMode)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              editMode ? 'bg-gray-200 text-gray-700' : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <Edit2 className="w-4 h-4" />
            {editMode ? 'Cancel' : 'Edit'}
          </button>
        </div>

        {editMode ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={handleSaveProfile}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Save Changes
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <User className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Full Name</p>
                <p className="font-medium text-gray-900">{profile?.user?.full_name || '-'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Mail className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium text-gray-900">{profile?.user?.email || '-'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Phone className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-medium text-gray-900">{profile?.user?.phone || profile?.client?.phone || '-'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Building className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Company</p>
                <p className="font-medium text-gray-900">{profile?.client?.company || '-'}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Password Change Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Lock className="w-5 h-5 text-gray-400" />
            <h3 className="font-semibold text-gray-900">Password</h3>
          </div>
          <button
            onClick={() => setShowPasswordForm(!showPasswordForm)}
            className="text-blue-600 hover:text-blue-700 font-medium text-sm"
          >
            {showPasswordForm ? 'Cancel' : 'Change Password'}
          </button>
        </div>

        {showPasswordForm && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
              <input
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
              <input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={handleChangePassword}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Update Password
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientPortal;
